<template>
  <div class="contract-list">
    <!-- 合同列表（企业微信手机端，仅查看） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="合同编号/合同名称" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-search-select v-model="searchInfo.customerId" :query="queryCustomerPage" size="small"
                              label-column="merchantRemarkName" code-column="id"
                              placeholder="客户" search-placeholder="客户名称/手机号"
                              @change="resetSearch">
          <template #item="{item}">
            <div class="mss-item">
              <div class="mss-item-main">{{ item.merchantRemarkName }}</div>
              <div class="mss-item-sub">{{ item.merchantRemarkPhone || "无联系电话" }}</div>
            </div>
          </template>
        </mobile-search-select>
        <mobile-condition-select v-model="searchInfo.contractType" :data="contractTypeOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="合同类型" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.contractStatus" :data="contractStatusOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="状态" @change="resetSearch"/>
        <mobile-search-select v-model="searchInfo.salesId" :query="queryEmployeePage" size="small"
                              label-column="employeeName" code-column="id"
                              :display-text="searchInfo.salesName" placeholder="销售人员"
                              search-placeholder="职员名称/手机号" @change="onSalesChange"/>
        <!-- 签约时间：未填签约日期时按创建时间过滤，与统计口径一致 -->
        <mobile-date-range-select v-model:start-date="searchInfo.startDateFrom"
                                  v-model:end-date="searchInfo.startDateTo"
                                  placeholder="签约时间" title="选择签约时间" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList"
                 :refresh="resetSearch" ref="mobileList">
      <list-card class="contract-item" v-for="item in dataList" :key="item.id" :label="item.contractName">
        <template #desc>
          <span class="status-tag" :class="'tag-' + statusOf(item.contractStatus).type">
            {{ statusOf(item.contractStatus).text }}
          </span>
        </template>
        <data-column label="合同编号">{{ item.contractNumber || "—" }}</data-column>
        <data-column label="客户名称">{{ item.customerName || "—" }}</data-column>
        <data-column label="合同类型">{{ contractTypeMap[item.contractType] || "—" }}</data-column>
        <data-column label="合同金额">{{ fmtMoney(item.amount) }} 元</data-column>
        <data-column label="已回款">{{ fmtMoney(item.amountReceived) }} 元</data-column>
        <data-column label="回款进度">{{ progressText(item.receiptProgress) }}</data-column>
        <data-column label="签约日期">{{ dateOnly(item.billDate) }}</data-column>
        <data-column label="负责人">{{ item.salesName || "—" }}</data-column>
      </list-card>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileDateRangeSelect from "@/components/qywx/data/MobileDateRangeSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import contractApi from "@/api/dz-crm/partner/contractApi";
import commonDataApi from "@/api/normal/common/commonDataApi";
import commonApi from "@/api/commonApi";
import {PARTNER_TYPE} from "@/views/h5/crm/crmDictionary";

const CONTRACT_TYPE_MAP = {1: "框架协议", 2: "销售合同", 3: "代理协议", 4: "服务合同"};
// 状态按 100 倍数递增：0草稿 100执行中 200已完成 300已逾期
const CONTRACT_STATUS_MAP = {
  0: {text: "草稿", type: "info"},
  100: {text: "执行中", type: "primary"},
  200: {text: "已完成", type: "success"},
  300: {text: "已逾期", type: "danger"},
};

const contractTypeOptions = Object.keys(CONTRACT_TYPE_MAP).map(value => ({
  value: Number(value),
  label: CONTRACT_TYPE_MAP[value],
}));
const contractStatusOptions = Object.keys(CONTRACT_STATUS_MAP).map(value => ({
  value: Number(value),
  label: CONTRACT_STATUS_MAP[value].text,
}));

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ContractListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileDateRangeSelect, MobileSearchSelect, MobileConditionSelect,
    MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        customerId: "",
        contractType: "",
        contractStatus: "",
        salesId: "",
        salesName: "",
        startDateFrom: "",
        startDateTo: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      contractTypeMap: CONTRACT_TYPE_MAP,
      contractTypeOptions,
      contractStatusOptions,
    };
  },
  created() {
    // 从销售目标页跳转过来：预置销售人员（含姓名回显）与目标周期
    const query = this.route.query || {};
    if (query.salesId) {
      this.searchInfo.salesId = query.salesId;
      this.searchInfo.salesName = query.salesName || "";
    }
    if (query.startTime) {
      this.searchInfo.startDateFrom = String(query.startTime).slice(0, 10);
    }
    if (query.endTime) {
      this.searchInfo.startDateTo = String(query.endTime).slice(0, 10);
    }
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      contractApi.queryList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    /** 空值不提交；起止日期换成后端字段 startTime/endTime */
    cleanParams() {
      const params = {};
      Object.keys(this.searchInfo).forEach(key => {
        const value = this.searchInfo[key];
        if (value === "" || value === null || value === undefined) {
          return;
        }
        if (key === "pageNumber" || key === "pageSize" || key === "hasMore"
            || key === "salesName" || key === "startDateFrom" || key === "startDateTo") {
          return;
        }
        params[key] = value;
      });
      if (this.searchInfo.startDateFrom) {
        params.startTime = this.searchInfo.startDateFrom;
      }
      if (this.searchInfo.startDateTo) {
        params.endTime = this.searchInfo.startDateTo;
      }
      return params;
    },
    // 切换销售人员后清掉外部回显文案，让选择框显示新选中的人
    onSalesChange(record) {
      this.searchInfo.salesName = record ? (record.employeeName || "") : "";
      this.resetSearch();
    },
    queryCustomerPage(params) {
      return commonDataApi.queryPartnerList({...params, partnerType: PARTNER_TYPE.CUSTOMER});
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    statusOf(status) {
      return CONTRACT_STATUS_MAP[status] || {text: "—", type: "info"};
    },
    progressText(value) {
      const n = Number(value) || 0;
      return Math.max(0, Math.min(100, Math.round(n * 100) / 100)) + "%";
    },
    fmtMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 0, maximumFractionDigits: 2});
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
  }
};
</script>

<style lang="scss" scoped>
.contract-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
// 类型 / 状态标签
.status-tag{
  flex: none;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: var(--yq-font-size-mini-mobile);
}
.tag-primary{
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}
.tag-info{
  color: var(--el-color-info);
  background: var(--el-fill-color-light);
}
.tag-success{
  color: var(--el-color-success);
  background: var(--el-color-success-light-9);
}
.tag-danger{
  color: var(--el-color-danger);
  background: var(--el-color-danger-light-9);
}
</style>
